import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { fmtNum, fmtINR } from "@/lib/format";
import { Plus, Trash2, Cpu, Wrench, GitBranch, ArrowUp, ArrowDown, Info } from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";

const MACHINE_TYPES = ["CNC", "VMC", "Lathe", "Press", "Injection", "Assembly", "Manual", "Other"];
const TOOL_TYPES = ["die", "jig", "fixture", "mould", "cutting_tool", "gauge", "other"];
const TOOL_STATUS = ["available", "in_use", "regrinding", "scrapped"];

function MachineTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = { code: "", name: "", department: "", machine_type: "CNC", capacity_per_shift: 0, capacity_uom: "nos",
                  shifts_per_day: 2, shift_hours: 8, mhr_rate: 0, labour_rate: 0, setup_time_min: 0,
                  is_bottleneck: false, is_active: true, remarks: "" };
  const [form, setForm] = useState(empty);

  const load = async () => setRows((await api.get("/mfg/machines")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.code || !form.name) return toast.error("Code and Name required");
    try {
      if (edit) await api.put(`/mfg/machines/${edit.id}`, form);
      else await api.post("/mfg/machines", form);
      toast.success("Saved"); setOpen(false); setEdit(null); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete machine?")) return;
    try { await api.delete(`/mfg/machines/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="mfg-machines-tab">
      <div className="flex justify-end">
        <Button onClick={() => { setEdit(null); setForm(empty); setOpen(true); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-machine-btn"><Plus className="w-3.5 h-3.5" /> New machine</Button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Code</th><th>Name</th><th>Type</th><th>Dept</th><th className="text-right">Cap/shift</th><th className="text-right">MHR ₹/hr</th><th className="text-right">Labour ₹/hr</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-muted-foreground">No machines yet — add your first work-center.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`machine-row-${r.code}`}>
                <td className="font-mono text-xs">{r.code}</td>
                <td className="font-medium">{r.name}{r.is_bottleneck && <span className="ml-2 text-[9px] font-mono uppercase text-red-500 border border-red-500/40 px-1.5 py-0.5 rounded-sm">bottleneck</span>}</td>
                <td className="text-[10px] font-mono uppercase text-accent">{r.machine_type || "—"}</td>
                <td className="text-xs">{r.department || "—"}</td>
                <td className="text-right tabular-nums text-xs">{fmtNum(r.capacity_per_shift)} {r.capacity_uom}</td>
                <td className="text-right tabular-nums">{fmtINR(r.mhr_rate)}</td>
                <td className="text-right tabular-nums">{fmtINR(r.labour_rate)}</td>
                <td><span className={`text-[10px] font-mono uppercase ${r.is_active ? "text-emerald-600" : "text-muted-foreground"}`}>{r.is_active ? "active" : "inactive"}</span></td>
                <td className="text-right">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setEdit(r); setForm({ ...r }); setOpen(true); }} data-testid={`edit-machine-${r.code}`}>Edit</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => del(r.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl rounded-none overflow-y-auto space-y-3">
          <SheetHeader><SheetTitle className="font-heading">{edit ? "Edit" : "New"} Machine</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" placeholder="CNC-01" data-testid="machine-code" /></div>
            <div><Label>Type</Label>
              <Select value={form.machine_type} onValueChange={(v) => setForm({ ...form, machine_type: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="machine-type"><SelectValue /></SelectTrigger>
                <SelectContent>{MACHINE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" data-testid="machine-name" /></div>
            <div><Label>Department</Label><Input value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value })} className="rounded-sm mt-1" placeholder="Machining · Assembly · Paint" /></div>
            <div><Label>Setup time (min)</Label><Input type="number" value={form.setup_time_min} onChange={(e) => setForm({ ...form, setup_time_min: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>Capacity / shift</Label><Input type="number" value={form.capacity_per_shift} onChange={(e) => setForm({ ...form, capacity_per_shift: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>UOM</Label><Input value={form.capacity_uom} onChange={(e) => setForm({ ...form, capacity_uom: e.target.value })} className="rounded-sm mt-1" placeholder="nos / kg" /></div>
            <div><Label>Shifts/day</Label><Input type="number" value={form.shifts_per_day} onChange={(e) => setForm({ ...form, shifts_per_day: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>Hours/shift</Label><Input type="number" value={form.shift_hours} onChange={(e) => setForm({ ...form, shift_hours: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>MHR ₹/hr</Label><Input type="number" value={form.mhr_rate} onChange={(e) => setForm({ ...form, mhr_rate: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" data-testid="machine-mhr" /></div>
            <div><Label>Labour ₹/hr</Label><Input type="number" value={form.labour_rate} onChange={(e) => setForm({ ...form, labour_rate: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div className="col-span-2 flex items-center justify-between border-t pt-3">
              <Label>Bottleneck (protect capacity)</Label>
              <Switch checked={form.is_bottleneck} onCheckedChange={(v) => setForm({ ...form, is_bottleneck: v })} />
            </div>
            <div className="col-span-2 flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
            </div>
            <div className="col-span-2"><Label>Remarks</Label><Input value={form.remarks || ""} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="rounded-sm mt-1" /></div>
          </div>
          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="machine-save">Save</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ToolTab() {
  const [rows, setRows] = useState([]);
  const [machines, setMachines] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = { code: "", name: "", tool_type: "cutting_tool", location: "",
                  life_cycles: 0, current_count: 0, regrind_count: 0, max_regrinds: 0,
                  cost: 0, status: "available", linked_machine_id: "", is_active: true };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setRows((await api.get("/mfg/tools")).data);
    setMachines((await api.get("/mfg/machines")).data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.code || !form.name) return toast.error("Code and Name required");
    const payload = { ...form, linked_machine_id: form.linked_machine_id || null };
    try {
      if (edit) await api.put(`/mfg/tools/${edit.id}`, payload);
      else await api.post("/mfg/tools", payload);
      toast.success("Saved"); setOpen(false); setEdit(null); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete tool?")) return;
    try { await api.delete(`/mfg/tools/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remaining = (t) => Math.max(0, (t.life_cycles || 0) - (t.current_count || 0));
  const lifePct = (t) => t.life_cycles ? Math.min(100, (t.current_count / t.life_cycles) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="mfg-tools-tab">
      <div className="flex justify-end">
        <Button onClick={() => { setEdit(null); setForm(empty); setOpen(true); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-tool-btn"><Plus className="w-3.5 h-3.5" /> New tool</Button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Code</th><th>Name</th><th>Type</th><th>Location</th><th className="text-right">Life</th><th className="text-right">Used</th><th className="text-right">Regrinds</th><th>Life %</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="10" className="text-center py-10 text-muted-foreground">No tools yet — track dies, jigs, cutting tools & their life.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`tool-row-${r.code}`}>
                <td className="font-mono text-xs">{r.code}</td>
                <td className="font-medium">{r.name}</td>
                <td className="text-[10px] font-mono uppercase text-accent">{r.tool_type}</td>
                <td className="text-xs">{r.location || "—"}</td>
                <td className="text-right tabular-nums text-xs">{fmtNum(r.life_cycles)}</td>
                <td className="text-right tabular-nums text-xs">{fmtNum(r.current_count)}</td>
                <td className="text-right tabular-nums text-xs">{r.regrind_count}/{r.max_regrinds || "—"}</td>
                <td>
                  <div className="w-24 h-1.5 bg-muted rounded-sm overflow-hidden">
                    <div className={`h-full ${lifePct(r) > 80 ? "bg-red-500" : lifePct(r) > 60 ? "bg-yellow-500" : "bg-emerald-500"}`} style={{ width: `${lifePct(r)}%` }} />
                  </div>
                  <div className="text-[9px] font-mono text-muted-foreground mt-0.5">{remaining(r)} left</div>
                </td>
                <td><span className={`text-[10px] font-mono uppercase ${r.status === "available" ? "text-emerald-600" : r.status === "scrapped" ? "text-red-500" : "text-yellow-500"}`}>{r.status}</span></td>
                <td className="text-right">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setEdit(r); setForm({ ...r, linked_machine_id: r.linked_machine_id || "" }); setOpen(true); }} data-testid={`edit-tool-${r.code}`}>Edit</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => del(r.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl rounded-none overflow-y-auto space-y-3">
          <SheetHeader><SheetTitle className="font-heading">{edit ? "Edit" : "New"} Tool</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" placeholder="T-001" data-testid="tool-code" /></div>
            <div><Label>Type</Label>
              <Select value={form.tool_type} onValueChange={(v) => setForm({ ...form, tool_type: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="tool-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TOOL_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" data-testid="tool-name" /></div>
            <div><Label>Location (tool crib)</Label><Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-sm mt-1" placeholder="Rack A-3" /></div>
            <div><Label>Cost ₹</Label><Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>Life (cycles)</Label><Input type="number" value={form.life_cycles} onChange={(e) => setForm({ ...form, life_cycles: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" data-testid="tool-life" /></div>
            <div><Label>Current count</Label><Input type="number" value={form.current_count} onChange={(e) => setForm({ ...form, current_count: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>Max regrinds</Label><Input type="number" value={form.max_regrinds} onChange={(e) => setForm({ ...form, max_regrinds: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>Regrind count</Label><Input type="number" value={form.regrind_count} onChange={(e) => setForm({ ...form, regrind_count: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div className="col-span-2"><Label>Linked machine</Label>
              <Select value={form.linked_machine_id || ""} onValueChange={(v) => setForm({ ...form, linked_machine_id: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="(none)" /></SelectTrigger>
                <SelectContent>{machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} · {m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{TOOL_STATUS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex items-center justify-between border-t pt-3">
              <Label>Active</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
            </div>
          </div>
          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="tool-save">Save</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function RoutingTab() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [tools, setTools] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [costOpen, setCostOpen] = useState(null);
  const emptyStep = () => ({ operation_code: "", operation_name: "", machine_id: "", tool_id: "",
                             cycle_time_min: 0, setup_time_min: 0, labour_min_per_unit: 0,
                             rejection_pct: 0, is_outsourced: false, remarks: "" });
  const empty = { code: "", name: "", item_id: "", version: "v1", is_active: true, steps: [emptyStep()] };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setRows((await api.get("/mfg/routings")).data);
    setItems((await api.get("/items")).data);
    setMachines((await api.get("/mfg/machines")).data);
    setTools((await api.get("/mfg/tools")).data);
  };
  useEffect(() => { load(); }, []);

  const addStep = () => setForm({ ...form, steps: [...form.steps, emptyStep()] });
  const removeStep = (i) => setForm({ ...form, steps: form.steps.filter((_, j) => j !== i) });
  const moveStep = (i, dir) => {
    const s = [...form.steps]; const j = i + dir;
    if (j < 0 || j >= s.length) return;
    [s[i], s[j]] = [s[j], s[i]]; setForm({ ...form, steps: s });
  };
  const updateStep = (i, key, val) => setForm({ ...form, steps: form.steps.map((s, j) => j === i ? { ...s, [key]: val } : s) });

  const save = async () => {
    if (!form.code || !form.name) return toast.error("Code and Name required");
    if (form.steps.length === 0 || !form.steps[0].operation_name) return toast.error("At least one operation required");
    const payload = {
      ...form,
      item_id: form.item_id || null,
      steps: form.steps.map((s) => ({ ...s, machine_id: s.machine_id || null, tool_id: s.tool_id || null })),
    };
    try {
      if (edit) await api.put(`/mfg/routings/${edit.id}`, payload);
      else await api.post("/mfg/routings", payload);
      toast.success("Saved"); setOpen(false); setEdit(null); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete routing?")) return;
    try { await api.delete(`/mfg/routings/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openCost = async (r) => {
    try { setCostOpen((await api.get(`/mfg/routings/${r.id}/cost`)).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="mfg-routings-tab">
      <div className="flex justify-end">
        <Button onClick={() => { setEdit(null); setForm({ ...empty, steps: [emptyStep()] }); setOpen(true); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-routing-btn"><Plus className="w-3.5 h-3.5" /> New routing</Button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Code</th><th>Name</th><th>FG Item</th><th>Version</th><th className="text-right">Steps</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-muted-foreground">No routings yet — define operation sequences for your products.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`routing-row-${r.code}`}>
                <td className="font-mono text-xs">{r.code}</td>
                <td className="font-medium">{r.name}</td>
                <td className="text-xs">{items.find((i) => i.id === r.item_id)?.name || "—"}</td>
                <td className="text-[10px] font-mono uppercase text-accent">{r.version}</td>
                <td className="text-right tabular-nums text-xs">{r.steps?.length || 0}</td>
                <td><span className={`text-[10px] font-mono uppercase ${r.is_active ? "text-emerald-600" : "text-muted-foreground"}`}>{r.is_active ? "active" : "inactive"}</span></td>
                <td className="text-right">
                  <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => openCost(r)} data-testid={`cost-routing-${r.code}`}><Info className="w-3.5 h-3.5" /> Cost</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setEdit(r); setForm({ ...r, item_id: r.item_id || "", steps: r.steps.map((s) => ({ ...s, machine_id: s.machine_id || "", tool_id: s.tool_id || "" })) }); setOpen(true); }}>Edit</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => del(r.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-3xl rounded-none overflow-y-auto space-y-3">
          <SheetHeader><SheetTitle className="font-heading">{edit ? "Edit" : "New"} Routing</SheetTitle></SheetHeader>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" data-testid="routing-code" /></div>
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" data-testid="routing-name" /></div>
            <div className="col-span-2"><Label>FG Item (optional)</Label>
              <Select value={form.item_id || ""} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="(any)" /></SelectTrigger>
                <SelectContent className="max-h-72"><div className="max-h-72 overflow-y-auto">{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</div></SelectContent>
              </Select>
            </div>
            <div><Label>Version</Label><Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} className="rounded-sm mt-1 font-mono" /></div>
          </div>

          <div className="border rounded-sm">
            <div className="px-3 py-2 border-b flex items-center justify-between">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Operations sequence</div>
              <Button variant="ghost" size="sm" onClick={addStep} className="rounded-sm gap-1 h-7"><Plus className="w-3.5 h-3.5" /> Op</Button>
            </div>
            <div className="max-h-[50vh] overflow-y-auto">
              {form.steps.map((s, i) => (
                <div key={i} className="border-b border-border/50 p-3 grid grid-cols-12 gap-2 items-end" data-testid={`step-${i}`}>
                  <div className="col-span-1 flex flex-col gap-1">
                    <span className="text-[10px] font-mono text-accent">{(i + 1) * 10}</span>
                    <button onClick={() => moveStep(i, -1)}><ArrowUp className="w-3 h-3" /></button>
                    <button onClick={() => moveStep(i, 1)}><ArrowDown className="w-3 h-3" /></button>
                  </div>
                  <div className="col-span-2"><Label className="text-[10px]">Op code</Label><Input value={s.operation_code} onChange={(e) => updateStep(i, "operation_code", e.target.value.toUpperCase())} className="rounded-sm h-8 mt-0.5 font-mono text-xs" placeholder="OP-10" /></div>
                  <div className="col-span-3"><Label className="text-[10px]">Operation</Label><Input value={s.operation_name} onChange={(e) => updateStep(i, "operation_name", e.target.value)} className="rounded-sm h-8 mt-0.5 text-xs" placeholder="Turning" /></div>
                  <div className="col-span-2"><Label className="text-[10px]">Machine</Label>
                    <Select value={s.machine_id || ""} onValueChange={(v) => updateStep(i, "machine_id", v)}>
                      <SelectTrigger className="rounded-sm h-8 mt-0.5 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.code}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label className="text-[10px]">Tool</Label>
                    <Select value={s.tool_id || ""} onValueChange={(v) => updateStep(i, "tool_id", v)}>
                      <SelectTrigger className="rounded-sm h-8 mt-0.5 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{tools.map((t) => <SelectItem key={t.id} value={t.id}>{t.code}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1"><Label className="text-[10px]">Cycle min</Label><Input type="number" value={s.cycle_time_min} onChange={(e) => updateStep(i, "cycle_time_min", Number(e.target.value))} className="rounded-sm h-8 mt-0.5 text-right tabular-nums text-xs" /></div>
                  <div className="col-span-1 flex items-end justify-center pb-1">{form.steps.length > 1 && <button onClick={() => removeStep(i)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>}</div>
                  <div className="col-span-2 col-start-2"><Label className="text-[10px]">Setup min</Label><Input type="number" value={s.setup_time_min} onChange={(e) => updateStep(i, "setup_time_min", Number(e.target.value))} className="rounded-sm h-8 mt-0.5 text-right tabular-nums text-xs" /></div>
                  <div className="col-span-2"><Label className="text-[10px]">Labour min/pc</Label><Input type="number" value={s.labour_min_per_unit} onChange={(e) => updateStep(i, "labour_min_per_unit", Number(e.target.value))} className="rounded-sm h-8 mt-0.5 text-right tabular-nums text-xs" /></div>
                  <div className="col-span-2"><Label className="text-[10px]">Rej %</Label><Input type="number" value={s.rejection_pct} onChange={(e) => updateStep(i, "rejection_pct", Number(e.target.value))} className="rounded-sm h-8 mt-0.5 text-right tabular-nums text-xs" /></div>
                  <div className="col-span-2 flex items-center gap-2 pb-1"><Switch checked={s.is_outsourced} onCheckedChange={(v) => updateStep(i, "is_outsourced", v)} /><Label className="text-[10px]">Outsourced</Label></div>
                  <div className="col-span-3"><Input value={s.remarks || ""} onChange={(e) => updateStep(i, "remarks", e.target.value)} className="rounded-sm h-8 text-xs" placeholder="Remarks" /></div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between border-t pt-3">
            <Label>Active</Label>
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
          </div>
          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="routing-save">Save</Button>
        </SheetContent>
      </Sheet>

      {/* Cost analysis dialog */}
      <Sheet open={!!costOpen} onOpenChange={(o) => !o && setCostOpen(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg rounded-none space-y-3 overflow-y-auto">
          {costOpen && (
            <>
              <SheetHeader><SheetTitle className="font-heading">Routing Cost · {costOpen.code}</SheetTitle></SheetHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Total cycle</div><div className="text-xl font-medium tabular-nums">{costOpen.total_cycle_time_min} min</div></div>
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Total setup</div><div className="text-xl font-medium tabular-nums">{costOpen.total_setup_time_min} min</div></div>
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-accent">MHR / unit</div><div className="text-xl font-medium tabular-nums">{fmtINR(costOpen.mhr_cost_per_unit)}</div></div>
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-accent">Labour / unit</div><div className="text-xl font-medium tabular-nums">{fmtINR(costOpen.labour_cost_per_unit)}</div></div>
                <div className="card-sharp p-3 col-span-2"><div className="text-[10px] font-mono uppercase text-emerald-600">Conversion cost / unit</div><div className="text-2xl font-medium tabular-nums">{fmtINR(costOpen.conversion_cost_per_unit)}</div></div>
                <div className="card-sharp p-3 col-span-2"><div className="text-[10px] font-mono uppercase text-red-500">Cumulative rejection</div><div className="text-xl font-medium tabular-nums">{costOpen.cumulative_rejection_pct}%</div></div>
              </div>
              <div className="card-sharp overflow-hidden">
                <table className="w-full dense">
                  <thead><tr><th>Seq</th><th>Operation</th><th>Machine</th><th className="text-right">MHR</th><th className="text-right">Lab</th></tr></thead>
                  <tbody>{costOpen.steps.map((s) => <tr key={s.seq}><td className="font-mono text-xs">{s.seq}</td><td className="text-xs">{s.operation}</td><td className="text-xs text-muted-foreground">{s.machine || "—"}</td><td className="text-right tabular-nums text-xs">{fmtINR(s.mhr_cost)}</td><td className="text-right tabular-nums text-xs">{fmtINR(s.labour_cost)}</td></tr>)}</tbody>
                </table>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default function MfgMasters() {
  const { hasModule } = useSubscription();
  if (!hasModule("mfg_masters")) {
    return (
      <div className="p-8" data-testid="mfg-locked">
        <div className="card-sharp p-10 text-center">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Enterprise Manufacturing</div>
          <div className="font-heading text-2xl mt-3">Manufacturing Masters are locked</div>
          <div className="text-muted-foreground text-sm mt-2 max-w-md mx-auto">
            Machine, Tool and Routing masters ship with the <strong>Enterprise Manufacturing</strong> plan.
            Upgrade to unlock work-center planning, tool life tracking, and standard vs actual costing.
          </div>
          <a href="/app/subscription" className="inline-block mt-5 px-5 py-2 bg-accent text-white rounded-sm text-sm font-medium" data-testid="upgrade-link">Upgrade →</a>
        </div>
      </div>
    );
  }
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="mfg-masters-page">
      <PageHeader eyebrow="Manufacturing" title="Work-Center Masters"
        description="Machines · tools · routings — the SAP-grade backbone that drives capacity planning, tool life, MHR costing and standard vs actual."
      />
      <Tabs defaultValue="machines">
        <TabsList className="rounded-sm">
          <TabsTrigger value="machines" className="rounded-sm gap-1.5" data-testid="tab-machines"><Cpu className="w-3.5 h-3.5" /> Machines</TabsTrigger>
          <TabsTrigger value="tools" className="rounded-sm gap-1.5" data-testid="tab-tools"><Wrench className="w-3.5 h-3.5" /> Tools</TabsTrigger>
          <TabsTrigger value="routings" className="rounded-sm gap-1.5" data-testid="tab-routings"><GitBranch className="w-3.5 h-3.5" /> Routings</TabsTrigger>
        </TabsList>
        <TabsContent value="machines" className="mt-4"><MachineTab /></TabsContent>
        <TabsContent value="tools" className="mt-4"><ToolTab /></TabsContent>
        <TabsContent value="routings" className="mt-4"><RoutingTab /></TabsContent>
      </Tabs>
    </div>
  );
}
